import { Grid, Typography } from '@mui/material';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { EMarkingTypes } from 'constants/Markings';
import { ETransactionTypes } from 'constants/TransactionTypes';
import { ITableColumns, ITableDataSource } from 'models/table';
import { ITransactionDetailsMarkings, ITransactionMarking } from 'models/transaction';

export enum EMarkingsLabels {
  fireCycleID = 'transactionDetails.markingDetails.accordion.fireCycleID.label',
  notApplicable = 'addTransaction.document.section.notApplicable.label',
  added = 'transactionDetails.markingDetails.accordion.added.label',
  importMarking = 'transactionDetails.markingDetails.accordion.importMarking.label',
  appliedMarking = 'transactionDetails.markingDetails.accordion.appliedMarking.label',
  surplusSaleMarking = 'transactionDetails.firearmDetails.accordion.surplusSaleMarking.label',
  yearOfTransfer = 'transactionDetails.firearmDetails.accordion.yearOfTransfer.label',
  confiscationMarking = 'transactionDetails.firearmDetails.accordion.confiscationMarking.label',
  yearOfConfiscation = 'transactionDetails.firearmDetails.accordion.yearOfConfiscation.label',
  exportMarking = 'transactionDetails.markingDetails.accordion.exportMarking.label',
  serialNumber = 'transactionDetails.markingDetails.accordion.serialNumber.label',
  lotNumber = 'transactionDetails.markingDetails.accordion.lotNumber.label',
  batchNumber = 'transactionDetails.markingDetails.accordion.batchNumber.label',
  before = 'general.before.label',
  after = 'general.after.label',
  deactivationMarking = 'transactionDetails.markingDetails.accordion.deactivationMarking.label',
}

export enum EAdditionalFields {
  SERIAL_NUMBER = 'serialNumber',
  YEAR_OF_CONFISCATION = 'yearOfConfiscation',
  YEAR_OF_TRANSFER = 'yearOfTransfer',
  BATCH_NUMBER = 'batchNumber',
  LOT_NUMBER = 'lotNumber',
}

export const getMarkingType = (
  typeUniqueId: string,
  markingType?: string,
): EMarkingTypes[] => {
  switch (typeUniqueId) {
    case ETransactionTypes.DECLARED_IMPORTED:
      return [EMarkingTypes.IMPORT];
    case ETransactionTypes.EXPORTED:
      return [EMarkingTypes.EXPORT];
    case ETransactionTypes.DEACTIVATED:
      return [EMarkingTypes.DEACTIVATION];
    case ETransactionTypes.MARKED:
      if (markingType === EMarkingTypes.APPLIED) return [EMarkingTypes.APPLIED];
      if (markingType === EMarkingTypes.IMPORT) return [EMarkingTypes.IMPORT];
      return [EMarkingTypes.APPLIED, EMarkingTypes.IMPORT];
    case ETransactionTypes.RECOVERED:
    case ETransactionTypes.DECLARED_MANUFACTURED:
    case ETransactionTypes.DECLARED_UNKNOWN_ORIGIN:
      return [EMarkingTypes.APPLIED];
    case ETransactionTypes.CONFISCATED:
      return [EMarkingTypes.CONFISCATION];
    case ETransactionTypes.SURPLUS_SALE:
      return [EMarkingTypes.SURPLUS_SALE];
    default:
      return [];
      break;
  }
};
export const getAdditionalFields = (
  typeUniqueId: string,
  isAmmunitionType: boolean,
): EAdditionalFields[] => {
  switch (typeUniqueId) {
    case ETransactionTypes.CONFISCATED:
      return [EAdditionalFields.YEAR_OF_CONFISCATION];
    case ETransactionTypes.SURPLUS_SALE:
      return [EAdditionalFields.YEAR_OF_TRANSFER];
    case ETransactionTypes.REPURPOSED:
      if (isAmmunitionType) {
        return [EAdditionalFields.BATCH_NUMBER, EAdditionalFields.LOT_NUMBER];
      }
      return [EAdditionalFields.SERIAL_NUMBER];
    default:
      return [];
  }
};

const isEmptyField = (object: any) =>
  object === undefined || object === null || object.toString().trim() === '';

const addedLabel = (before: any, after: any, t: any) => {
  if (!isEmptyField(after) && before !== after) {
    return `(${t(EMarkingsLabels.added)})`;
  }
  return '';
};

interface IRenderMarking {
  markings: ITransactionMarking[];
  type: EMarkingTypes;
  label: string;
  emptyString?: string;
  addedLabel?: boolean;
  t: any;
  fieldsForTransactionType: string[];
}
interface IRenderAddtionalField {
  value: string;
  label: string;
  t: any;
  fieldsForTransactionType: string[];
  type: EAdditionalFields;
}

export const RenderMarking = ({
  markings,
  type,
  label,
  emptyString = '-',
  addedLabel = false,
  t,
  fieldsForTransactionType,
}: IRenderMarking) => {
  if (!fieldsForTransactionType.includes(type)) {
    return '';
  }
  const markingData: ITransactionMarking | undefined = markings.find(
    (marking: ITransactionMarking) => marking.markingType === type,
  );

  const addedLabelVal =
    addedLabel && markingData && markingData.addedByTransaction
      ? ` (${t(EMarkingsLabels.added)})`
      : '';

  return (
    <Grid sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
        {t(label)}
      </Typography>
      <Typography variant="body2">
        {markingData ? markingData.marking : emptyString}
        <i>{addedLabelVal}</i>
      </Typography>
    </Grid>
  );
};
export const RenderAdditionalField = ({
  value,
  label,
  t,
  fieldsForTransactionType,
  type,
}: IRenderAddtionalField) => {
  if (!fieldsForTransactionType.includes(type)) {
    return '';
  }

  return (
    <Grid sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
        {t(label)}
      </Typography>
      <Typography variant="body2">{emptyFieldHolder(value)}</Typography>
    </Grid>
  );
};

export const mapTransactionDetailsMarkings = (
  items: ITransactionDetailsMarkings[],
): ITableDataSource[] =>
  items.map((item) => ({
    id: item.fireCycleId,
    fireCycleId: item.fireCycleId,
    serialNumber: item.serialNumber,
    previousSerialNumber: item.previousSerialNumber,
    batchNumber: item.batchNumber,
    previousBatchNumber: item.previousBatchNumber,
    lotNumber: item.lotNumber,
    previousLotNumber: item.previousLotNumber,
    markings: item.markings,
    yearOfConfiscation: item.yearOfConfiscation,
    yearOfTransfer: item.yearOfTransfer,
    wasAssetImported: item.wasAssetImported,
  }));

export const transactionDetailsMarkingsTableColumns = (
  t: any,
  fieldsForTransactionType: (EMarkingTypes | EAdditionalFields)[],
  transactionType?: ETransactionTypes,
): ITableColumns[] => {
  return [
    {
      id: 'f0504ca-6b32-482d-b369-404a6f32f511',
      key: 'fireCycleId',
      title: 'transactionDetails.markingDetails.accordion.fireCycleID.label',
    },
    ...(fieldsForTransactionType.includes(EMarkingTypes.IMPORT)
      ? [
        {
          id: 'f0504ca6-6b32-482d-b369-404a6f32f532',
          key: 'importMarking',
          title: EMarkingsLabels.importMarking,
          render: (_data: any, props: any) => {
            const importMarking: ITransactionMarking | undefined = props?.markings.find(
              (marking: ITransactionMarking) => marking.markingType === EMarkingTypes.IMPORT,
            );
            const addLabel = fieldsForTransactionType.includes(EMarkingTypes.APPLIED);
            return importMarking ? (
              <>
                {emptyFieldHolder(importMarking.marking)}
                <i>
                  {importMarking.addedByTransaction &&
                    addLabel &&
                    ` (${t(EMarkingsLabels.added)})`}
                </i>
              </>
            ) : props.wasAssetImported ||
              (transactionType && transactionType !== ETransactionTypes.MARKED) ? (
              '-'
            ) : (
              t(EMarkingsLabels.notApplicable)
            );
          },
        },
      ]
      : []),
    ...(fieldsForTransactionType.includes(EMarkingTypes.APPLIED)
      ? [
        {
          id: 'f0504ca6-6b32-482d-b369-404a6f32f543',
          key: 'appliedMarking',
          title: EMarkingsLabels.appliedMarking,
          render: (_data: any, props: any) => {
            const appliedMarking: ITransactionMarking | undefined = props?.markings.find(
              (marking: ITransactionMarking) =>
                marking.markingType === EMarkingTypes.APPLIED,
            );
            const addLabel = fieldsForTransactionType.includes(EMarkingTypes.IMPORT);
            return appliedMarking ? (
              <>
                {emptyFieldHolder(appliedMarking.marking)}
                <i>
                  {appliedMarking.addedByTransaction &&
                    addLabel &&
                    ` (${t(EMarkingsLabels.added)})`}
                </i>
              </>
            ) : (
              '-'
            );
          },
        },
      ]
      : []),

    ...(fieldsForTransactionType.includes(EMarkingTypes.CONFISCATION)
      ? [
        {
          id: 'f0504ca6-6b32-482d-b369-404a6f32f545',
          key: 'confiscationMarking',
          title: EMarkingsLabels.confiscationMarking,
          render: (_data: any, props: any) => {
            const markingData: ITransactionMarking | undefined = props?.markings.find(
              (marking: ITransactionMarking) =>
                marking.markingType === EMarkingTypes.CONFISCATION,
            );
            return emptyFieldHolder(markingData?.marking);
          },
        },
      ]
      : []),
    ...(fieldsForTransactionType.includes(EAdditionalFields.YEAR_OF_CONFISCATION)
      ? [
        {
          id: 'f0504ca6-6b32-482d-b369-404a6f32f546',
          key: 'yearOfConfiscation',
          title: EMarkingsLabels.yearOfConfiscation,
          render: (_data: any, props: any) => {
            return emptyFieldHolder(props.yearOfConfiscation);
          },
        },
      ]
      : []),

    ...(fieldsForTransactionType.includes(EMarkingTypes.SURPLUS_SALE)
      ? [
        {
          id: 'f0504ca6-6b32-482d-b369-404a6f32f547',
          key: 'surplusSaleMarking',
          title: EMarkingsLabels.surplusSaleMarking,
          render: (_data: any, props: any) => {
            const markingData: ITransactionMarking | undefined = props?.markings.find(
              (marking: ITransactionMarking) =>
                marking.markingType === EMarkingTypes.SURPLUS_SALE,
            );
            return emptyFieldHolder(markingData?.marking);
          },
        },
      ]
      : []),
    ...(fieldsForTransactionType.includes(EAdditionalFields.YEAR_OF_TRANSFER)
      ? [
        {
          id: 'f0504ca6-6b32-482d-b369-404a6f32f548',
          key: 'yearOfTransfer',
          title: EMarkingsLabels.yearOfTransfer,
          render: (_data: any, props: any) => {
            return emptyFieldHolder(props.yearOfTransfer);
          },
        },
      ]
      : []),
    ...(fieldsForTransactionType.includes(EMarkingTypes.EXPORT)
      ? [
        {
          id: 'f0504ca6-6b32-482d-b369-404a6f32f549',
          key: 'exportMarking',
          title: EMarkingsLabels.exportMarking,
          render: (_data: any, props: any) => {
            const markingData: ITransactionMarking | undefined = props?.markings.find(
              (marking: ITransactionMarking) => marking.markingType === EMarkingTypes.EXPORT,
            );
            return emptyFieldHolder(markingData?.marking);
          },
        },
      ]
      : []),
    ...(fieldsForTransactionType.includes(EMarkingTypes.DEACTIVATION)
      ? [
        {
          id: 'f0504ca6-6b32-482d-b369-404a6f32f552',
          key: 'deactivationMarking',
          title: EMarkingsLabels.deactivationMarking,
          render: (_data: any, props: any) => {
            const markingData: ITransactionMarking | undefined = props?.markings.find(
              (marking: ITransactionMarking) =>
                marking.markingType === EMarkingTypes.DEACTIVATION,
            );
            return emptyFieldHolder(markingData?.marking);
          },
        },
      ]
      : []),
    ...(fieldsForTransactionType.includes(EAdditionalFields.SERIAL_NUMBER)
      ? [
        {
          id: 'f0504ca6-6b32-482d-b369-404a6f32f553',
          key: 'serialNumberBefore',
          isSortable: false,
          title: `${t(EMarkingsLabels.serialNumber)} (${t(EMarkingsLabels.before)})`,
          render: (_data: any, props: any) => {
            return emptyFieldHolder(props.previousSerialNumber);
          },
        },
        {
          id: 'f0504ca6-6b32-482d-b369-404a6f32f553',
          key: 'serialNumberAfter',
          isSortable: false,
          title: `${t(EMarkingsLabels.serialNumber)} (${t(EMarkingsLabels.after)})`,
          render: (_data: any, props: any) => {
            return `${emptyFieldHolder(isEmptyField(props.serialNumber) ? props.previousSerialNumber : props.serialNumber)} ${addedLabel(props.previousSerialNumber, props.serialNumber, t)}`;
          },
        },
      ]
      : []),
    ...(fieldsForTransactionType.includes(EAdditionalFields.LOT_NUMBER)
      ? [
        {
          id: 'f0504ca6-6b32-482d-b369-404a6f32f553',
          key: 'lotNumberBefore',
          isSortable: false,
          title: `${t(EMarkingsLabels.lotNumber)} (${t(EMarkingsLabels.before)})`,
          render: (_data: any, props: any) => {
            return emptyFieldHolder(props.previousLotNumber);
          },
        },
        {
          id: 'f0504ca6-6b32-482d-b369-404a6f32f553',
          key: 'lotNumberAfter',
          isSortable: false,
          title: `${t(EMarkingsLabels.lotNumber)} (${t(EMarkingsLabels.after)})`,
          render: (_data: any, props: any) => {
            return `${emptyFieldHolder(isEmptyField(props.lotNumber) ? props.previousLotNumber : props.lotNumber)} ${addedLabel(props.previousLotNumber, props.lotNumber, t)}`;
          },
        },
      ]
      : []),
    ...(fieldsForTransactionType.includes(EAdditionalFields.BATCH_NUMBER)
      ? [
        {
          id: 'f0504ca6-6b32-482d-b369-404a6f32f553',
          key: 'batchNumberBefore',
          isSortable: false,
          title: `${t(EMarkingsLabels.batchNumber)} (${t(EMarkingsLabels.before)})`,
          render: (_data: any, props: any) => {
            return emptyFieldHolder(props.previousBatchNumber);
          },
        },
        {
          id: 'f0504ca6-6b32-482d-b369-404a6f32f553',
          key: 'batchNumberAfter',
          isSortable: false,
          title: `${t(EMarkingsLabels.batchNumber)} (${t(EMarkingsLabels.after)})`,
          render: (_data: any, props: any) => {
            return `${emptyFieldHolder(isEmptyField(props.batchNumber) ? props.previousBatchNumber : props.batchNumber)} ${addedLabel(props.previousBatchNumber, props.batchNumber, t)}`;
          },
        },
      ]
      : []),
  ];
};
